import React, { useState, useEffect } from 'react'
import { apiGet, apiPost, getErrorMessage } from '../../utils/api'

interface TransferModalProps {
  isOpen: boolean
  chatId: string
  currentAgentId?: string
  onClose: () => void
  onTransferred: () => void
}

export const TransferModal: React.FC<TransferModalProps> = ({ isOpen, chatId, currentAgentId, onClose, onTransferred }) => {
  const [agents, setAgents] = useState<any[]>([])
  const [selectedAgent, setSelectedAgent] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setError(null)
      setSelectedAgent('')
      apiGet<{ agents: any[] }>('/agents').then(r => {
        // Filter out the current agent — can't transfer to yourself
        const available = (r.agents || []).filter((a: any) => a.id !== currentAgentId)
        setAgents(available)
      }).catch(() => {
        setError('Failed to load agents')
      })
    }
  }, [isOpen, currentAgentId])

  const handleTransfer = async () => {
    if (!selectedAgent) return
    setLoading(true)
    setError(null)
    try {
      await apiPost(`/chats/${chatId}/transfer`, { agent_id: selectedAgent })
      onTransferred()
      onClose()
    } catch (err) {
      console.error('Transfer failed:', err)
      setError(getErrorMessage(err, 'Transfer failed'))
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-surface rounded-xl shadow-float border border-border w-full max-w-md mx-4 animate-slide-up">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-txt-primary">Transfer Chat</h3>
          <p className="text-sm text-txt-secondary mt-0.5">Reassign this conversation to another agent</p>
        </div>

        <div className="px-5 py-4 space-y-4">
          {error && (
            <div className="text-sm text-error bg-error/10 rounded-lg px-3 py-2">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-txt-primary mb-1.5">Transfer to Agent</label>
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20"
            >
              <option value="">Select an agent...</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name} — {a.open_chats || 0} active chat{(a.open_chats || 0) !== 1 ? 's' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-surface-secondary/50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-txt-secondary hover:text-txt-primary hover:bg-surface-hover transition-colors">
            Cancel
          </button>
          <button
            onClick={handleTransfer}
            disabled={!selectedAgent || loading}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40 transition-colors"
          >
            {loading ? 'Transferring...' : 'Transfer'}
          </button>
        </div>
      </div>
    </div>
  )
}
